import { useNavigate, useParams } from "react-router-dom";
import { motion } from "framer-motion";
import { ArrowLeft, Lock } from "lucide-react";
import { useEffect, useState } from "react";

/* ---------------- DUMMY CONTENT PER COURSE ---------------- */

const COURSE_CONTENT = {
  1: {
    title: "Calm Your Mind",
    desc: "Learn simple techniques to quiet mental noise and restore calm.",
    paragraphs: [
      "This course focuses on slowing down racing thoughts and reducing mental overload. Many people experience constant inner noise without realizing how deeply it affects their peace of mind.",
      "You will explore simple awareness practices that help you observe thoughts without being pulled into them. This creates space between stimulus and response.",
      "Over time, these practices can reduce mental tension and improve emotional clarity, helping you feel more grounded throughout the day.",
      "The emphasis is on simplicity and consistency rather than force or control. Calm grows naturally when attention becomes gentle and steady."
    ],
  },

  2: {
    title: "Anxiety Reset",
    desc: "A step-by-step approach to reducing daily anxiety.",
    paragraphs: [
      "Anxiety often builds silently through small habits and reactions. This course helps you recognize those patterns and interrupt them early.",
      "You will learn how anxious thoughts form and how the body responds to perceived threats, even when no real danger exists.",
      "By understanding these cycles, you can begin to reset your nervous system and reduce the intensity of anxious reactions.",
      "With regular practice, anxiety becomes more manageable and less disruptive to daily life."
    ],
  },

  3: {
    title: "Panic Attack Toolkit",
    desc: "Tools to manage panic episodes safely and calmly.",
    paragraphs: [
      "Panic attacks can feel overwhelming, but they are temporary physiological responses. This course explains what happens during panic and why it is not dangerous.",
      "You will learn grounding techniques that help stabilize breathing and redirect attention during moments of panic.",
      "Understanding panic reduces fear of recurrence, which is often the strongest trigger for future episodes.",
      "With practice, confidence increases and panic loses its control over daily life."
    ],
  },

  4: {
    title: "Sleep & Relaxation",
    desc: "Improve sleep quality through relaxation techniques.",
    paragraphs: [
      "Quality sleep depends on the ability to release tension at the end of the day. This course focuses on calming both mind and body before rest.",
      "You will explore relaxation methods that gently slow mental activity and prepare the nervous system for sleep.",
      "Better sleep improves emotional regulation, focus, and overall resilience.",
      "Small changes in nighttime routines can lead to significant improvements in sleep quality."
    ],
  },

  5: {
    title: "Emotional Balance",
    desc: "Understand and regulate emotional responses.",
    paragraphs: [
      "Emotions provide important information, but when unmanaged they can feel overwhelming. This course helps you understand emotional patterns.",
      "You will learn how emotions arise and how to respond without suppression or overreaction.",
      "Balanced emotional awareness leads to better decision-making and healthier relationships.",
      "The goal is not to eliminate emotions, but to relate to them with clarity and stability."
    ],
  },
};

/* ---------------- COMPONENT ---------------- */

export default function CourseDetail() {
  const { courseId } = useParams();
  const navigate = useNavigate();

  const isPremium = localStorage.getItem("isPremium") === "true";
  const isLocked = courseId % 2 === 0 && !isPremium;

  const [course, setCourse] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const timer = setTimeout(() => {
      const data =
        COURSE_CONTENT[courseId] || {
          title: `Course #${courseId}`,
          desc:
            "A guided program designed to support calm, awareness, and emotional well-being.",
          paragraphs: [
            "This course offers thoughtful guidance to help you slow down and reflect.",
            "The focus is on awareness, consistency, and emotional clarity.",
            "Each section encourages gentle progress rather than quick fixes.",
            "Growth happens gradually, and this space supports that process."
          ],
        };

      setCourse({
        ...data,
        image: `/courses/course-${((courseId % 12) || 1)}.jpg`,
      });

      setLoading(false);
    }, 300);

    return () => clearTimeout(timer);
  }, [courseId]);

  if (loading) {
    return (
      <div className="page-scroll p-6 safe-top animate-pulse">
        <div className="h-40 bg-[#E8F5E9] rounded-3xl" />
      </div>
    );
  }

  if (isLocked) {
    return (
      <div className="page-scroll p-6 safe-top text-center">
        <Lock className="w-10 h-10 mx-auto text-[#7A9B8D]" />
        <h1 className="text-xl font-bold text-[#1A3C2F] mt-4">
          Premium Course
        </h1>
        <p className="text-[#4A6B5D] mt-2">
          This course is available for premium users.
        </p>
        <button
          onClick={() => navigate("/courses")}
          className="btn-primary mt-4"
        >
          Back to Courses
        </button>
      </div>
    );
  }

  return (
    <div className="page-scroll safe-top">
      {/* HERO */}
      <div className="relative h-48">
        <img
          src={course.image}
          alt={course.title}
          className="w-full h-full object-cover"
          onError={(e) => (e.currentTarget.src = "/courses/course-1.jpg")}
        />
        <div className="absolute inset-0 bg-gradient-to-t from-[#1A3C2F]/80 to-transparent" />

        <button
          onClick={() => navigate("/courses")}
          className="absolute top-4 left-4 w-10 h-10 rounded-full bg-white/80 flex items-center justify-center"
        >
          <ArrowLeft className="w-5 h-5 text-[#1A3C2F]" />
        </button>

        <div className="absolute bottom-4 left-4 right-4">
          <h1 className="text-white text-2xl font-bold">
            {course.title}
          </h1>
          <p className="text-white/80 text-sm">
            {course.desc}
          </p>
        </div>
      </div>

      {/* CONTENT */}
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        className="p-6 space-y-6 text-[#4A6B5D] leading-relaxed"
      >
        {course.paragraphs.map((text, index) => (
          <p key={index}>{text}</p>
        ))}
      </motion.div>
    </div>
  );
}
